import { Component, DestroyRef, effect, inject, Input, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { ChildLevel, EndpointsParameterPwc, Koc_Kd, KocKd, RelationLevel } from './eea-endpoints-parameters-pwc.inteface';
import { DataSet } from 'src/app/shared/models/echo/data-set';
import { EeaEndpointsParametersPwcLogicService } from './eea-endpoints-parameters-pwc.logic.service';
import { Constants } from 'src/app/shared/utils/constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TabMenuLogicService } from 'src/app/shared/services/tab-menu.logic.service';
import { EndpointGet, EndpointSaveFormat } from 'src/app/shared/models/endpoint';
import { EndpointsConfigurationPwc } from './eea-endpoints-parameters-pwc.config';
import { EndpointDatasetLogicService } from 'src/app/shared/components/endpoint-dataset/endpoint-dataset.logic.service';
import { Utils } from 'src/app/shared/utils/utils';

@Component({
  selector: 'app-eea-endpoints-parameters-pwc',
  templateUrl: './eea-endpoints-parameters-pwc.component.html',
  styleUrls: ['./eea-endpoints-parameters-pwc.component.css']
})
export class EeaEndpointsParametersPwcComponent implements OnInit, OnDestroy {

  @Input({ required: true }) compartment!: string;
  @Input({ required: true }) dataset!: DataSet;
  @Input({ required: true }) isProjectOwnershipValid: boolean = false;
  @Input({ required: true }) menuService!: TabMenuLogicService;
  @Input({ required: true }) subTabMenuService!: TabMenuLogicService;

  public configuration: EndpointsParameterPwc[] = [];
  public parent?: ChildLevel;
  public daugther?: ChildLevel;
  public grandDaugther?: ChildLevel;
  public koc_kd: KocKd = { value: Koc_Kd.Koc, modified: true };
  public relationLevelParent = RelationLevel.Parent;
  public relationLevelDaugther = RelationLevel.Daugther;
  public relationLevelGrandDaugther = RelationLevel.GrandDaugther;
  public blockUi: boolean = false;
  private destroyRef = inject(DestroyRef);
  private logicService = inject(EeaEndpointsParametersPwcLogicService);
  private endpointLogicService = inject(EndpointDatasetLogicService);
  private isFirstLoad: boolean = true;
  private isSaving: boolean = false;
  private savePromiseResolver?: () => void;

  isExpandedDatasetEffect = effect(() => {
    const isDatasetExpanded = this.endpointLogicService.isDatasetExpanded();

    if (!isDatasetExpanded && !this.isSaving && !this.isFirstLoad) {
      this.isSaving = true;
      this.setSaveData()
        .then(() => {
          if (this.savePromiseResolver) {
            this.savePromiseResolver();
            this.savePromiseResolver = undefined;
          }
        })
        .catch((error) => {
          console.error(error);
          if (this.savePromiseResolver) {
            this.savePromiseResolver();
            this.savePromiseResolver = undefined;
          }
        })
        .finally(() => {
          this.isSaving = false;
        });
    }
  }, { allowSignalWrites: true });

  public async ngOnInit(): Promise<void> {
    this.blockUi = true;
    const isPwcV2 = this.endpointLogicService.isPwcV2;

    const configs = EndpointsConfigurationPwc.CONFIGURATION
      .filter(config => !isPwcV2 || !config.hideInV2)
      .map(config => isPwcV2 && config.displayNameV2 ? { ...config, displayName: config.displayNameV2 } : config);

    configs.forEach(config => {
      this.configuration.push({
        ...config,
        parentModified: false,
        daughterModified: false,
        grandDaughterModified: false,
        moleDaughterModified: false,
        moleGranDautherModified: false
      });
    });

    this.initSubscribes();
    await this.setChildLevelDb();
    await this.GetEndpoints();
    this.isFirstLoad = false;
    this.blockUi = false;
  }

  private initSubscribes(): void {
    this.menuService.activeIndexChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isFirstLoad)
        this.setSaveData();
    });
    this.subTabMenuService.activeIndexChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isFirstLoad)
        this.setSaveData();
    });
  }

  private async setChildLevelDb(): Promise<void> {
    if (!this.dataset.dataSetPk) return;

    this.parent = {
      childLevel: Constants.CHILD_LEVEL.PARENT,
      metabolitePk: this.dataset.compoundPk!,
      metaboliteName: this.dataset.activeIngredient!,
      isActive: true
    }

    const childrenLevel = await this.logicService.getChildLevelByDataset(this.dataset.dataSetPk);
    this.daugther = childrenLevel.find(child => child.childLevel === Constants.CHILD_LEVEL.DAUGHTER);
    this.grandDaugther = childrenLevel.find(child => child.childLevel === Constants.CHILD_LEVEL.GRANDDAUGHTER);
  }

  private async GetEndpoints(): Promise<void> {

    if (!this.dataset.dataSetPk) return;

    const endpointsDb = await this.logicService.getEndpoints(this.dataset.dataSetPk);

    endpointsDb.forEach(endpointDb => {
      if (endpointDb.endpointFieldValue.toLowerCase() === Constants.DATA_VALUES_NAMES.isKoc.toLowerCase()) {
        this.koc_kd.modified = false;
        this.koc_kd.value = endpointDb.value === 1 ? Koc_Kd.Koc : Koc_Kd.Kd;
        this.logicService.updateIsKd(endpointDb.value === 0 ? true : false);
        this.koc_kd.endpointPk = endpointDb.endpointPk;
        this.configuration = this.configuration.filter(c => c.endpointFieldValue !== Constants.DATA_VALUES_NAMES.isKoc);
      }

      this.configuration.forEach(config => {

        if (endpointDb.moleculePk === this.parent?.metabolitePk && this.parent.isActive && !endpointDb.metabolitePk && config.endpointFieldValue.toLowerCase() === endpointDb.endpointFieldValue.toLowerCase()) {
          this.setCommonFieldsFromDb(config, endpointDb);
          config.parentEndpointPk = endpointDb.endpointPk;
          config.parentValue = config.exponential ? this.setExponentialNumber(endpointDb.value) : endpointDb.value;
          config.parentModified = false;
        }

        if (endpointDb.metabolitePk === this.daugther?.metabolitePk && endpointDb.endpointFieldValue.includes('Moles') && endpointDb.endpointFieldValue.toLowerCase().includes(config.endpointFieldValue.toLowerCase())) {
          this.setCommonFieldsFromDb(config, endpointDb);
          config.moleDaugtherEndpointPk = endpointDb.endpointPk;
          config.daughterMetabolitePk = endpointDb.metabolitePk;
          config.moleDaugtherValue = endpointDb.value;
          config.moleDaughterModified = false;
        }

        if (endpointDb.metabolitePk === this.daugther?.metabolitePk && config.endpointFieldValue.toLowerCase() === endpointDb.endpointFieldValue.toLowerCase()) {
          this.setCommonFieldsFromDb(config, endpointDb);
          config.daugtherEndpointPk = endpointDb.endpointPk;
          config.daughterMetabolitePk = endpointDb.metabolitePk;
          config.daugtherValue = config.exponential ? this.setExponentialNumber(endpointDb.value) : endpointDb.value;
          config.daughterModified = false;
        }

        if (endpointDb.metabolitePk === this.grandDaugther?.metabolitePk && endpointDb.endpointFieldValue.includes('Moles') && endpointDb.endpointFieldValue.toLowerCase().includes(config.endpointFieldValue.toLowerCase())) {
          this.setCommonFieldsFromDb(config, endpointDb);
          config.moleGranddaughterEndpointPk = endpointDb.endpointPk;
          config.grandDaughterMetabolitePk = endpointDb.metabolitePk;
          config.moleGrandDaughterValue = endpointDb.value;
          config.moleGranDautherModified = false;
        }

        if (endpointDb.metabolitePk === this.grandDaugther?.metabolitePk && config.endpointFieldValue.toLowerCase() === endpointDb.endpointFieldValue.toLocaleLowerCase()) {
          this.setCommonFieldsFromDb(config, endpointDb);
          config.granddaughterEndpointPk = endpointDb.endpointPk;
          config.grandDaughterMetabolitePk = endpointDb.metabolitePk;
          config.granDaugtherValue = config.exponential ? this.setExponentialNumber(endpointDb.value) : endpointDb.value;
          config.grandDaughterModified = false;
        }
      });

    });
  }

  private setCommonFieldsFromDb(config: EndpointsParameterPwc, endpointDb: EndpointGet): void {
    config.datasetPk = endpointDb.datasetPk;
    config.moleculePk = endpointDb.moleculePk;
  }

  public onKocKdClick(): void {
    this.koc_kd.modified = true;
    this.logicService.updateIsKd(this.koc_kd.value === Koc_Kd.Koc ? true : false);
  }

  public setAsModified(relationLevel: RelationLevel, field: EndpointsParameterPwc): void {
    field.moleculePk = this.dataset.compoundPk;
    field.datasetPk = this.dataset.dataSetPk;

    switch (relationLevel) {
      case RelationLevel.Parent:
        field.parentModified = true;
        return;
      case RelationLevel.Daugther:
        field.daughterModified = true;
        field.daughterMetabolitePk = this.daugther?.metabolitePk;
        return;
      case RelationLevel.GrandDaugther:
        field.grandDaughterModified = true;
        field.grandDaughterMetabolitePk = this.grandDaugther?.metabolitePk;
        return;
    }
  }

  public setAsModifiedMole(relationLevel: RelationLevel, field: EndpointsParameterPwc): void {
    switch (relationLevel) {
      case RelationLevel.Daugther:
        field.moleDaughterModified = true;
        return;
      case RelationLevel.GrandDaugther:
        field.moleGranDautherModified = true;
        return;
    }
  }

  private async setSaveData(): Promise<void> {
    this.blockUi = true;

    try {
      const moleculePk = this.parent?.metabolitePk!;
      const saveData: EndpointSaveFormat[] = [];

      if (this.koc_kd.modified) {
        saveData.push(this.setKocKdRow(moleculePk));
      }

      if (saveData !== undefined)
        this.configuration.forEach(config => {

          config.moleculePk = moleculePk;

          if (config.daughterMetabolitePk && config.daughterMetabolitePk != this.daugther?.metabolitePk && (config.daugtherValue || config.moleDaugtherValue)) {
            config.daughterMetabolitePk = this.daugther?.metabolitePk;

            if (config.daugtherValue)
              config.daughterModified = true;

            if (config.moleDaugtherValue)
              config.moleDaughterModified = true;
          }

          if (config.grandDaughterMetabolitePk && config.grandDaughterMetabolitePk != this.grandDaugther?.metabolitePk && (config.granDaugtherValue || config.moleGrandDaughterValue)) {
            config.grandDaughterMetabolitePk = this.grandDaugther?.metabolitePk;

            if (config.granDaugtherValue)
              config.grandDaughterModified = true;

            if (config.moleGrandDaughterValue)
              config.moleGranDautherModified = true;
          }

          if (config.parentModified) {
            saveData.push(this.setRowToSave(config, config.parentValue, undefined, config.parentEndpointPk));
          }

          if (config.moleDaughterModified) {
            const endpointFieldValue = `Moles ${config.endpointFieldValue} Daughter`;
            saveData.push(this.setMolesRow(endpointFieldValue, config.moleDaugtherValue, this.daugther?.metabolitePk, config.moleculePk!, config.moleDaugtherEndpointPk));
          }

          if (config.daughterModified) {
            saveData.push(this.setRowToSave(config, config.daugtherValue, this.daugther?.metabolitePk, config.daugtherEndpointPk));
          }

          if (config.moleGranDautherModified) {
            const endpointFieldValue = `Moles ${config.endpointFieldValue} Granddaughter`;
            saveData.push(this.setMolesRow(endpointFieldValue, config.moleGrandDaughterValue, this.grandDaugther?.metabolitePk, config.moleculePk!, config.moleGranddaughterEndpointPk));
          }

          if (config.grandDaughterModified) {
            saveData.push(this.setRowToSave(config, config.granDaugtherValue, this.grandDaugther?.metabolitePk, config.granddaughterEndpointPk));
          }

        });

      if (saveData.length == 0) {
        return;
      }

      await this.logicService.saveAndUpdateEndpoints(saveData);
      this.resetModifiedFlags();
    } catch (error) {
      console.error( error);
      throw error;
    } finally {
      this.blockUi = false;
    }
  }

  private setKocKdRow(moleculePk: number): EndpointSaveFormat {
    const row: EndpointSaveFormat = {
      endpointPk: this.koc_kd.endpointPk,
      compartment: this.compartment,
      datasetPk: this.dataset.dataSetPk!,
      dataSource: Constants.THIS_APPLICATION_ENDPOINT_DATA_SOURCE,
      endpointFieldValue: Constants.DATA_VALUES_NAMES.isKoc,
      moleculePk: moleculePk,
      value: this.koc_kd.value === Koc_Kd.Koc ? 1 : 0
    }
    return row;
  }

  private resetModifiedFlags(): void {
    this.koc_kd.modified = false;
    this.configuration.forEach(config => {
      config.parentModified = false;
      config.daughterModified = false;
      config.grandDaughterModified = false;
      config.moleDaughterModified = false;
      config.moleGranDautherModified = false;
    });
  }

  private setMolesRow(endpointFieldValue: string, value: number | undefined, metabolitePk: number | undefined, moleculePk: number, endpointPk: number | undefined): EndpointSaveFormat {
    const row: EndpointSaveFormat = {
      compartment: this.compartment,
      datasetPk: this.dataset.dataSetPk!,
      dataSource: Constants.THIS_APPLICATION_ENDPOINT_DATA_SOURCE,
      endpointFieldValue: endpointFieldValue,
      endpointPk: endpointPk,
      metabolitePk: metabolitePk,
      moleculePk: moleculePk,
      value: value
    }
    return row;
  }

  private setRowToSave(config: EndpointsParameterPwc, value: number | string | undefined, metabolitePk: number | undefined, endpointPk: number | undefined): EndpointSaveFormat {
    const row: EndpointSaveFormat = {
      compartment: this.compartment,
      datasetPk: config.datasetPk!,
      dataSource: Constants.THIS_APPLICATION_ENDPOINT_DATA_SOURCE,
      endpointFieldValue: config.endpointFieldValue,
      endpointPk: endpointPk,
      metabolitePk: metabolitePk,
      moleculePk: config.moleculePk!,
      value: config.exponential ? this.convertExponentialToNumber(value) : value as number | undefined
    }
    return row;
  }

  public ngOnDestroy(): void {
    this.endpointLogicService.setIsDatasetExpanded(false);
    this.isExpandedDatasetEffect.destroy();
  }

  public onBlurExponentialInput(displayName: string, relationLevel: RelationLevel): void {
    const row = this.configuration.find(c => c.displayName === displayName);

    switch (relationLevel) {
      case RelationLevel.Parent:
        row!.parentValue = this.setExponentialNumber(row?.parentValue);
        break;
      case RelationLevel.Daugther:
        row!.daugtherValue = this.setExponentialNumber(row?.daugtherValue);
        break;
      case RelationLevel.GrandDaugther:
        row!.granDaugtherValue = this.setExponentialNumber(row?.granDaugtherValue);
        break;
    }
  }

  private setExponentialNumber(pValue: number | undefined | string): number | undefined {
    if (!isNaN(Number(pValue)))
      return Utils.convertNumberToExponential(pValue);
    else
      return undefined;
  }

  private convertExponentialToNumber(pValue: number | undefined | string): number | undefined {
    if (pValue)
      return Utils.convertExponentialToNumber(pValue);
    else
      return undefined;
  }

  updateIsKoc(value: boolean) {
       this.logicService.updateIsKd(value);
  }
}
